import {
  AppStoreConstructorArgs,
  AppSubStore,
  AppSubStoreArgs,
} from "@/store/types";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { AppStoreQueriesCacheStore } from "@/store/queries/AppStoreQueriesCacheStore";
import { AppStoreSpaceStore } from "@/store/space/AppStoreSpaceStore";
import { AppStoreSpaceAccountStore } from "@/store/space-account/AppStoreSpaceAccountStore";
import { AppStoreRoutingStore } from "@/store/routing/AppStoreRoutingStore";
import { AppStoreNavigationStore } from "@/store/navigation/AppStoreNavigationStore";
import { AppStoreAccountStore } from "@/store/account/AppStoreAccountStore";
import pRetry from "p-retry";
import { initializeAccountObservability } from "@/store/utils/initialization";
import { BaseError, StoreError } from "@/domains/errors";
import { PublicAppStore } from "@/store/PublicAppStore";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import localDb from "@/domains/local-db";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { AppStorePlatformInfoStore } from "@/store/platform-info/AppStorePlatformInfoStore";
import { AppStoreSyncStore } from "@/store/sync/AppStoreSyncStore";
import { AppStoreLocalBlobStoreStore } from "@/store/local-blob-store/AppStoreLocalBlobStoreStore";
import { AppStoreNoteStore } from "@/store/note/AppStoreNoteStore";
import { AppStoreCollectionsStore } from "@/store/collections/AppStoreCollectionsStore";
import { AppStoreCollectionItemsStore } from "@/store/collection-items/AppStoreCollectionItemsStore";
import { AppStoreFavoriteItemsStore } from "@/store/favorite-items/AppStoreFavoriteItemsStore";
import { AppStoreFeedbackStore } from "@/store/feedback";
import { AppStoreSavedSearchesStore } from "@/store/saved-searches/AppStoreSavedSearchesStore";
import { AppStoreSpaceAccountNotesStore } from "@/store/recent-items/AppStoreSpaceAccountNotesStore";
import { AppStoreSpaceAccountCollectionsStore } from "@/store/recent-items/AppStoreSpaceAccountCollectionsStore";
import { AppStoreRecentItemsStore } from "@/store/recent-items/AppStoreRecentItemsStore";
import { AppStoreContactsStore } from "@/store/contacts/AppStoreContactsStore";
import { AppStoreCollectionMetadataStore } from "@/store/collections/AppStoreCollectionMetadataStore";
import { AppStoreSpaceAccountContactsStore } from "@/store/contacts/AppStoreSpaceAccountContactsStore";
import { ModalsStore } from "@/store/modals/ModalsStore";
import { ProactiveLostAccessModalStore } from "@/store/modals/ProactiveLostAccessModalStore";
import { AppStoreNoteContentDocumentStore } from "@/store/note/AppStoreNoteContentDocumentStore";
import { AppStoreSpaceAccountFeatureFlagsStore } from "./feature-flags/AppStoreSpaceAccountFeatureFlagsStore";
import { AppStoreFloatingAgentStore } from "./floating-agent/AppStoreFloatingAgentStore";
import { windowModule } from "@/modules/window";
import { MemDB } from "@/domains/dexie-db";
import { clientEnvModule } from "@/modules/client-env";
import { electronModule } from "@/modules/electron";
import type { DesktopUpdateRestartBlocker } from "@/modules/electron/types";
import { Search } from "@/domains/search";
import { AppStoreDataExportsStore } from "@/store/data-exports/AppStoreDataExportsStore";
import { AppStoreSpaceAccountAppCalloutsStore } from "@/store/app-callout/AppStoreSpaceAccountAppCalloutsStore";
import { AppStoreApiKeysStore } from "@/store/api-keys/AppStoreApiKeysStore";
import { AppStoreSpaceAccountTemplateStore } from "@/store/templates/AppStoreSpaceAccountTemplateStore";
import { AppStoreTemplateContentDocumentStore } from "@/store/templates/AppStoreTemplateContentDocumentStore";
import { AppStoreTemplateStore } from "@/store/templates/AppStoreTemplateStore";
import { LogoutState } from "@/components/sync/types";
import { TabLifecycleManager, TabLifecycleState } from "@/store/tab-lifecycle";
import { TabDetector } from "@/store/tab-detector";
import { AppStoreSessionStore } from "@/store/sessions/AppStoreSessionStore";
import { AppStoreSessionEventStore } from "@/store/sessions/AppStoreSessionEventStore";
import { AppStoreNoteSourceStore } from "@/store/sources/AppStoreNoteSourceStore";
import { AppStoreUploadedFileBatchStore } from "@/store/uploaded-files/AppStoreUploadedFileBatchStore";
import { AppStoreUploadedFileStore } from "@/store/uploaded-files/AppStoreUploadedFileStore";
import { AppStoreSourceStore } from "@/store/sources/AppStoreSourceStore";
import { AppStoreUploadProgressStore } from "@/store/upload-progress/AppStoreUploadProgressStore";
import { AppStoreEmailSendersStore } from "@/store/email-senders/AppStoreEmailSendersStore";
import { IEmailSenderStore } from "@/store/email-senders/types";
import { AppStoreZapierIntegrationStore } from "@/store/zapier-integration/AppStoreZapierIntegrationStore";
import { IZapierIntegrationStore } from "@/store/zapier-integration/types";
import { AppStoreAudioRecordingStore } from "@/store/audio-recordings";
import { AppStoreSubscriptionStore } from "@/store/subscriptions";
import { IChromeExtensionStore } from "@/store/chrome-extension/types";
import { AppStoreChromeExtensionStore } from "@/store/chrome-extension/AppStoreChromeExtensionStore";
import { AppStoreFeatureGatesStore } from "@/store/feature-gates/AppStoreFeatureGatesStore";
import { AppStoreHeadsUpStore } from "@/store/heads-up/AppStoreHeadsUpStore";
import { DatabaseManager } from "@/domains/tanstack-db";
import { AppStoreLayoutStore } from "@/store/layout/AppStoreLayoutStore";
import type { PageStore } from "@/store/PageStore";
import { isMemAccount } from "@/store/contacts/isMemAccount";
import { AppStoreDexieStore } from "@/store/repositories/AppStoreDexieStore";
import {
  identifyUserInMetrics,
  MetricsBrowserMetadata,
  trackAccountSignupCompletedIfRecent,
  trackMobileWebLoginCompleted,
} from "@/domains/metrics";
import { AppStoreMetricsStore } from "@/store/metrics/AppStoreMetricsStore";
import { AppStoreAudioStore } from "@/store/audio/audio.store";
import { AppStoreCalendarStore } from "@/store/calendar";
import { CacheStore } from "@/modules/cache";
import { getApiAuthIdentifier } from "@/modules/api/authUtils";
import {
  ACCOUNT_CACHE_DB_IDENTIFIER,
  CacheKey,
} from "@/modules/cache/constants";
import { AppStoreGettingStartedStore } from "@/store/getting-started/AppStoreGettingStartedStore";
import { AppStoreSpaceAccountCalendarStore } from "@/store/calendar/space-account-calendar/AppStoreSpaceAccountCalendarStore";
import { AppStoreSpaceAccountCalendarConnectionStore } from "@/store/calendar/space-account-calendar-connection/AppStoreSpaceAccountCalendarConnectionStore";
import { AppStoreSpaceAccountConnectionsStore } from "@/store/connections/AppStoreSpaceAccountConnectionsStore";
import { AppStoreSpaceAccountCalendarEventStore } from "@/store/calendar/space-account-calendar-event/AppStoreSpaceAccountCalendarEventStore";
import { AppStoreSpaceAccountSettingsStore } from "@/store/space-account-settings";
import { AppStoreSpaceAccountNoteAttachmentsStore } from "@/store/note-attachments";
import { AppStoreOneClickImportStore } from "@/store/one-click-import/AppStoreOneClickImportStore";
import { OnboardingStore } from "@/store/onboarding";
import { CalendarOnboardingStore } from "@/store/onboarding/CalendarOnboardingStore";
import { SettingsModalStore } from "@/store/settings-modal/SettingsModalStore";
import { SettingsModalTemplatesPageStore } from "@/store/settings-modal/SettingsModalTemplatesPageStore";
import { AppStoreMemAgentStore } from "@/store/mem-agent/mem-agent-store";
import { AppStoreMemAgentCalloutStore } from "@/store/mem-agent/mem-agent-callout-store";
import { AgentOverlayStore } from "@/store/agent-overlay/agent-overlay-store";
import {
  AppStoreAssistantFollowUpsStore,
  AppStoreAssistantProjectsStore,
  AppStoreAssistantRoutinesStore,
  AppStoreAssistantSkillsStore,
  AppStoreAssistantSyncModelStore,
  AppStoreAssistantTasksStore,
  AssistantConversationItemModelData,
  AssistantConversationModelData,
  AssistantNoteActivityModelData,
  AssistantNoteReviewStatusModelData,
  AssistantObjectiveModelData,
  AssistantProjectActivityModelData,
  AssistantProjectLabelAssignmentModelData,
  AssistantProjectLabelGroupModelData,
  AssistantProjectLabelModelData,
  AssistantSpaceAccountSettingsModelData,
  AssistantTaskActivityModelData,
  AssistantTaskLabelAssignmentModelData,
  AssistantTaskLabelGroupModelData,
  AssistantTaskLabelModelData,
  AssistantTaskNoteReferenceModelData,
  assistantSyncModelIndexers,
} from "@/store/assistant";
import { SyncModelKind } from "@/store/sync/types";
import type { Uuid } from "@/domains/global/identifiers";
import { sleep } from "@/domains/event-loop/synchronization";
import {
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingUploadStatus,
  type IndexedAudioRecordingSyncUpdateValue,
} from "@/store/audio-recordings";

interface ResetStorageAndReloadOptions {
  /**
   * Keep the unsent outbound operation queue across a client reset.
   *
   * `processing` contains serialized sync operations that have not been
   * acknowledged by the backend yet. When CLIENT_RESET_REQUIRED is caused by a
   * stale CVR/client-group cursor, those operations are still the user's local
   * edits and are not represented anywhere in server history. Dropping this
   * table can permanently lose offline note edits.
   *
   * This does not preserve `pending` or `optimisticUpdates`:
   * - `pending` operations were already accepted by the backend and should be
   *   reconciled from the fresh bootstrap/sync stream after reset.
   * - `optimisticUpdates` are derived local projections of queued operations
   *   against the pre-reset model cache, so keeping them can reapply stale
   *   snapshots after the fresh server state is loaded.
   */
  preserveOutboundProcessingQueue?: boolean;

  /**
   * Keep local audio payload tables and leave a one-shot recovery marker.
   *
   * Audio recovery is different from note text recovery. The queued operation
   * says that a recording exists, but the actual user payload is local data:
   * web artifact chunks/manifests, transcript turns, or legacy blob rows. On a
   * CLIENT_RESET_REQUIRED reload we rotate the client group ID, so old
   * AUDIO_RECORDING rows would normally be ignored by the boot reconciler. This
   * option records the old client group and the local recording IDs that had
   * recoverable evidence before reset, then lets audio initialization consume
   * that marker after the fresh bootstrap.
   */
  preserveLocalAudioRecoveryState?: boolean;
}

export class AppStore {
  db: DatabaseManager;
  apiKeys: AppStoreApiKeysStore;
  account: AppStoreAccountStore;
  accountCache: CacheStore;
  audio: AppStoreAudioStore;
  audioRecordings: AppStoreAudioRecordingStore;
  calendar: AppStoreCalendarStore;
  calendarOnboarding: CalendarOnboardingStore;
  collectionItems: AppStoreCollectionItemsStore;
  collectionMetadata: AppStoreCollectionMetadataStore;
  collections: AppStoreCollectionsStore;
  contacts: AppStoreContactsStore;
  dataExports: AppStoreDataExportsStore;
  favoriteItems: AppStoreFavoriteItemsStore;
  feedback: AppStoreFeedbackStore;
  modals: ModalsStore;
  navigation: AppStoreNavigationStore;
  localBlobStore: AppStoreLocalBlobStoreStore;
  notes: AppStoreNoteStore;
  noteContentDocuments: AppStoreNoteContentDocumentStore;
  oneClickImport: AppStoreOneClickImportStore;
  platformInfo: AppStorePlatformInfoStore;
  onboarding: OnboardingStore;

  proactiveLostAccessModal: ProactiveLostAccessModalStore;
  publicAppStore: PublicAppStore;
  queriesCache: AppStoreQueriesCacheStore;
  recentItems: AppStoreRecentItemsStore;
  routing: AppStoreRoutingStore;
  savedSearches: AppStoreSavedSearchesStore;
  spaceAccountAppCallouts: AppStoreSpaceAccountAppCalloutsStore;
  spaceAccountCollections: AppStoreSpaceAccountCollectionsStore;
  spaceAccountContacts: AppStoreSpaceAccountContactsStore;
  spaceAccountNotes: AppStoreSpaceAccountNotesStore;
  spaceAccountFeatureFlags: AppStoreSpaceAccountFeatureFlagsStore;
  floatingAgent: AppStoreFloatingAgentStore;
  spaceAccountTemplates: AppStoreSpaceAccountTemplateStore;
  spaceAccountCalendars: AppStoreSpaceAccountCalendarStore;
  spaceAccountCalendarConnections: AppStoreSpaceAccountCalendarConnectionStore;
  spaceAccountConnections: AppStoreSpaceAccountConnectionsStore;
  spaceAccountCalendarEvents: AppStoreSpaceAccountCalendarEventStore;
  spaceAccountSettings: AppStoreSpaceAccountSettingsStore;
  spaceAccountNoteAttachments: AppStoreSpaceAccountNoteAttachmentsStore;
  spaceAccounts: AppStoreSpaceAccountStore;
  spaces: AppStoreSpaceStore;
  sync: AppStoreSyncStore;
  templates: AppStoreTemplateStore;
  templateContentDocuments: AppStoreTemplateContentDocumentStore;
  sessions: AppStoreSessionStore;
  sessionEvents: AppStoreSessionEventStore;
  sources: AppStoreSourceStore;
  noteSources: AppStoreNoteSourceStore;
  uploadedFiles: AppStoreUploadedFileStore;
  uploadedFileBatches: AppStoreUploadedFileBatchStore;
  uploadProgress: AppStoreUploadProgressStore;
  subscriptions: AppStoreSubscriptionStore;
  featureGates: AppStoreFeatureGatesStore;
  headsUp: AppStoreHeadsUpStore;
  layout: AppStoreLayoutStore;
  pageStore?: PageStore;
  dexie: AppStoreDexieStore;
  metrics: AppStoreMetricsStore;
  gettingStarted: AppStoreGettingStartedStore;
  settingsModal: SettingsModalStore;
  settingsModalTemplatesPage: SettingsModalTemplatesPageStore;
  agentOverlay: AgentOverlayStore;
  memAgent: AppStoreMemAgentStore;
  memAgentCallout: AppStoreMemAgentCalloutStore;
  assistantTasks: AppStoreAssistantTasksStore;
  assistantProjects: AppStoreAssistantProjectsStore;
  assistantFollowUps: AppStoreAssistantFollowUpsStore;
  assistantTaskLabelGroups: AppStoreAssistantSyncModelStore<AssistantTaskLabelGroupModelData>;
  assistantTaskLabels: AppStoreAssistantSyncModelStore<AssistantTaskLabelModelData>;
  assistantTaskLabelAssignments: AppStoreAssistantSyncModelStore<AssistantTaskLabelAssignmentModelData>;
  assistantTaskNoteReferences: AppStoreAssistantSyncModelStore<AssistantTaskNoteReferenceModelData>;
  assistantProjectLabelGroups: AppStoreAssistantSyncModelStore<AssistantProjectLabelGroupModelData>;
  assistantProjectLabels: AppStoreAssistantSyncModelStore<AssistantProjectLabelModelData>;
  assistantProjectLabelAssignments: AppStoreAssistantSyncModelStore<AssistantProjectLabelAssignmentModelData>;
  assistantTaskActivities: AppStoreAssistantSyncModelStore<AssistantTaskActivityModelData>;
  assistantProjectActivities: AppStoreAssistantSyncModelStore<AssistantProjectActivityModelData>;
  assistantNoteActivities: AppStoreAssistantSyncModelStore<AssistantNoteActivityModelData>;
  assistantRoutines: AppStoreAssistantRoutinesStore;
  assistantSkills: AppStoreAssistantSkillsStore;
  assistantObjectives: AppStoreAssistantSyncModelStore<AssistantObjectiveModelData>;
  assistantNoteReviewStatuses: AppStoreAssistantSyncModelStore<AssistantNoteReviewStatusModelData>;
  assistantSpaceAccountSettings: AppStoreAssistantSyncModelStore<AssistantSpaceAccountSettingsModelData>;
  assistantConversations: AppStoreAssistantSyncModelStore<AssistantConversationModelData>;
  assistantConversationItems: AppStoreAssistantSyncModelStore<AssistantConversationItemModelData>;

  accountLoadingState: AsyncResult<boolean> = asyncResultModule.setLoading();
  mountedState: AsyncResult<boolean> = asyncResultModule.setLoading();
  search: Search;
  tabLifecycleManager: TabLifecycleManager;
  tabDetector: TabDetector;
  emailSenders: IEmailSenderStore;
  chromeExtension: IChromeExtensionStore;
  zapierIntegration: IZapierIntegrationStore;

  protected _memDb: MemDB | undefined;
  /** Shares one MemDB open attempt across concurrent foreground lifecycle callbacks. */
  private memDbOpenPromise: Promise<boolean> | null = null;
  private _readyForMetricsInitialization: boolean = false;
  private _metricsBrowserMetadata: MetricsBrowserMetadata | null = null;
  private _metricsBrowserMetadataUnsubscribe: (() => void) | null = null;

  constructor({
    publicAppStore,
    api,
    pusher,
    database,
  }: AppStoreConstructorArgs) {
    const injectedDeps: AppSubStoreArgs = { store: this, api, pusher };

    this.publicAppStore = publicAppStore;
    this.db = database ?? new DatabaseManager();
    this.tabLifecycleManager = new TabLifecycleManager();
    this.tabDetector = new TabDetector();
    this.apiKeys = new AppStoreApiKeysStore(injectedDeps);
    this.account = new AppStoreAccountStore(injectedDeps);
    this.accountCache = new CacheStore({
      databaseId: ACCOUNT_CACHE_DB_IDENTIFIER,
      scopeResolver: getApiAuthIdentifier,
    });
    this.dexie = new AppStoreDexieStore(injectedDeps);

    // These need to be initialized before any other stores that depend on them.
    this.modals = new ModalsStore(injectedDeps);
    this.navigation = new AppStoreNavigationStore(injectedDeps);

    this.audio = new AppStoreAudioStore(injectedDeps);
    this.calendar = new AppStoreCalendarStore(injectedDeps);
    this.audioRecordings = new AppStoreAudioRecordingStore(injectedDeps);
    this.collectionItems = new AppStoreCollectionItemsStore(injectedDeps);
    this.collectionMetadata = new AppStoreCollectionMetadataStore(injectedDeps);
    this.collections = new AppStoreCollectionsStore(injectedDeps);
    this.contacts = new AppStoreContactsStore(injectedDeps);
    this.dataExports = new AppStoreDataExportsStore(injectedDeps);
    this.favoriteItems = new AppStoreFavoriteItemsStore(injectedDeps);
    this.feedback = new AppStoreFeedbackStore(injectedDeps);
    this.localBlobStore = new AppStoreLocalBlobStoreStore(injectedDeps);
    this.notes = new AppStoreNoteStore(injectedDeps);
    this.noteContentDocuments = new AppStoreNoteContentDocumentStore(
      injectedDeps,
    );
    this.oneClickImport = new AppStoreOneClickImportStore(injectedDeps);
    this.platformInfo = new AppStorePlatformInfoStore(injectedDeps);
    this.calendarOnboarding = new CalendarOnboardingStore(injectedDeps);
    this.onboarding = new OnboardingStore(injectedDeps);

    this.queriesCache = new AppStoreQueriesCacheStore();
    this.recentItems = new AppStoreRecentItemsStore(injectedDeps);
    this.routing = new AppStoreRoutingStore(injectedDeps);
    this.savedSearches = new AppStoreSavedSearchesStore(injectedDeps);
    this.spaceAccountAppCallouts = new AppStoreSpaceAccountAppCalloutsStore(
      injectedDeps,
    );
    this.spaceAccountCollections = new AppStoreSpaceAccountCollectionsStore(
      injectedDeps,
    );
    this.spaceAccountContacts = new AppStoreSpaceAccountContactsStore(
      injectedDeps,
    );
    this.spaceAccountNotes = new AppStoreSpaceAccountNotesStore(injectedDeps);
    this.spaceAccountFeatureFlags = new AppStoreSpaceAccountFeatureFlagsStore(
      injectedDeps,
    );
    this.floatingAgent = new AppStoreFloatingAgentStore(injectedDeps);
    this.spaceAccountTemplates = new AppStoreSpaceAccountTemplateStore(
      injectedDeps,
    );
    this.spaceAccountCalendars = new AppStoreSpaceAccountCalendarStore(
      injectedDeps,
    );
    this.spaceAccountCalendarConnections =
      new AppStoreSpaceAccountCalendarConnectionStore(injectedDeps);
    this.spaceAccountConnections = new AppStoreSpaceAccountConnectionsStore(
      injectedDeps,
    );
    this.spaceAccountCalendarEvents =
      new AppStoreSpaceAccountCalendarEventStore(injectedDeps);
    this.spaceAccountSettings = new AppStoreSpaceAccountSettingsStore(
      injectedDeps,
    );
    this.spaceAccountNoteAttachments =
      new AppStoreSpaceAccountNoteAttachmentsStore(injectedDeps);
    this.spaceAccounts = new AppStoreSpaceAccountStore(injectedDeps);
    this.spaces = new AppStoreSpaceStore(injectedDeps);
    this.search = new Search({ store: this });
    this.templates = new AppStoreTemplateStore(injectedDeps);
    this.templateContentDocuments = new AppStoreTemplateContentDocumentStore(
      injectedDeps,
    );
    this.sessions = new AppStoreSessionStore(injectedDeps);
    this.sessionEvents = new AppStoreSessionEventStore(injectedDeps);
    this.sources = new AppStoreSourceStore(injectedDeps);
    this.noteSources = new AppStoreNoteSourceStore(injectedDeps);
    this.uploadedFileBatches = new AppStoreUploadedFileBatchStore(injectedDeps);
    this.uploadedFiles = new AppStoreUploadedFileStore(injectedDeps);
    this.uploadProgress = new AppStoreUploadProgressStore(injectedDeps);
    this.subscriptions = new AppStoreSubscriptionStore(injectedDeps);
    this.featureGates = new AppStoreFeatureGatesStore(injectedDeps);
    this.headsUp = new AppStoreHeadsUpStore(injectedDeps);
    this.layout = new AppStoreLayoutStore(injectedDeps);
    this.metrics = new AppStoreMetricsStore(injectedDeps);
    this.gettingStarted = new AppStoreGettingStartedStore(injectedDeps);
    this.settingsModal = new SettingsModalStore(injectedDeps);
    this.settingsModalTemplatesPage = new SettingsModalTemplatesPageStore(
      injectedDeps,
    );
    this.agentOverlay = new AgentOverlayStore(injectedDeps);
    this.memAgentCallout = new AppStoreMemAgentCalloutStore(injectedDeps);
    this.memAgent = new AppStoreMemAgentStore();
    this.assistantTasks = new AppStoreAssistantTasksStore(injectedDeps);
    this.assistantProjects = new AppStoreAssistantProjectsStore(injectedDeps);
    this.assistantFollowUps = new AppStoreAssistantFollowUpsStore(injectedDeps);
    this.assistantTaskLabelGroups = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantTaskLabelGroup,
      computeModelIndexes: assistantSyncModelIndexers.taskLabelGroup,
      ...injectedDeps,
    });
    this.assistantTaskLabels = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantTaskLabel,
      computeModelIndexes: assistantSyncModelIndexers.taskLabel,
      ...injectedDeps,
    });
    this.assistantTaskLabelAssignments = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantTaskLabelAssignment,
      computeModelIndexes: assistantSyncModelIndexers.taskLabelAssignment,
      ...injectedDeps,
    });
    this.assistantTaskNoteReferences = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantTaskNoteReference,
      computeModelIndexes: assistantSyncModelIndexers.taskNoteReference,
      ...injectedDeps,
    });
    this.assistantProjectLabelGroups = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantProjectLabelGroup,
      computeModelIndexes: assistantSyncModelIndexers.projectLabelGroup,
      ...injectedDeps,
    });
    this.assistantProjectLabels = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantProjectLabel,
      computeModelIndexes: assistantSyncModelIndexers.projectLabel,
      ...injectedDeps,
    });
    this.assistantProjectLabelAssignments = new AppStoreAssistantSyncModelStore(
      {
        modelKind: SyncModelKind.AssistantProjectLabelAssignment,
        computeModelIndexes: assistantSyncModelIndexers.projectLabelAssignment,
        ...injectedDeps,
      },
    );
    this.assistantTaskActivities = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantTaskActivity,
      computeModelIndexes: assistantSyncModelIndexers.taskActivity,
      ...injectedDeps,
    });
    this.assistantProjectActivities = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantProjectActivity,
      computeModelIndexes: assistantSyncModelIndexers.projectActivity,
      ...injectedDeps,
    });
    this.assistantNoteActivities = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantNoteActivity,
      computeModelIndexes: assistantSyncModelIndexers.noteActivity,
      ...injectedDeps,
    });
    this.assistantRoutines = new AppStoreAssistantRoutinesStore(injectedDeps);
    this.assistantSkills = new AppStoreAssistantSkillsStore(injectedDeps);
    this.assistantObjectives = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantObjective,
      computeModelIndexes: assistantSyncModelIndexers.objective,
      ...injectedDeps,
    });
    this.assistantNoteReviewStatuses = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantNoteReviewStatus,
      computeModelIndexes: assistantSyncModelIndexers.noteReviewStatus,
      ...injectedDeps,
    });
    this.assistantSpaceAccountSettings = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantSpaceAccountSettings,
      computeModelIndexes: assistantSyncModelIndexers.spaceAccountSettings,
      ...injectedDeps,
    });
    this.assistantConversations = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantConversation,
      computeModelIndexes: assistantSyncModelIndexers.conversation,
      ...injectedDeps,
    });
    this.assistantConversationItems = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantConversationItem,
      computeModelIndexes: assistantSyncModelIndexers.conversationItem,
      ...injectedDeps,
    });

    // This needs to be initialized after all the stores are initialized
    this.sync = new AppStoreSyncStore(injectedDeps);
    makeAutoObservable<this, "memDbOpenPromise">(this, {
      memDbOpenPromise: false,
    });

    // We need this to be already observable to react to changes its observable props.
    this.proactiveLostAccessModal = new ProactiveLostAccessModalStore(
      injectedDeps,
    );
    this.emailSenders = new AppStoreEmailSendersStore(this);
    this.chromeExtension = new AppStoreChromeExtensionStore(this);
    this.zapierIntegration = new AppStoreZapierIntegrationStore(this);

    // Track ui_ready event when store becomes ready
    reaction(
      () => this.readyState.data,
      (isReady) => {
        this.metrics.trackUiReady({ isReady });
      },
    );
  }

  public setPageStore(pageStore: PageStore) {
    this.pageStore = pageStore;
  }

  get auth() {
    return this.publicAppStore.auth;
  }

  get interface() {
    return this.publicAppStore.interface;
  }

  get debug() {
    return this.publicAppStore.debug;
  }

  get electron() {
    return this.publicAppStore.electron;
  }

  get systemPermissions() {
    return this.publicAppStore.systemPermissions;
  }

  get globalCache() {
    return this.publicAppStore.globalCache;
  }

  get memDb(): MemDB {
    if (!this._memDb) {
      throw new Error("Attempt to access MemDB before initialization");
    }

    return this._memDb;
  }

  get isMemDbReady() {
    return this._memDb !== undefined;
  }

  /**
   * Fetches data which is critical to our logged-in routes:
   * - Account info for the current user
   * - Spaces for the current user
   * - SpaceAccounts for the current user
   *
   * Note that the UI will block while these promises complete.
   * (Ideally, they implement some form of caching/persistence)
   */
  async initialize() {
    try {
      this.metrics.trackAppStoreInitialize();

      runInAction(() => {
        this.accountLoadingState = asyncResultModule.setLoading();
      });

      if (!this.auth.isAuthenticated) {
        throw new StoreError({
          message: "User must be authenticated to initialize the AppStore.",
        });
      }

      const retryOptions = {
        retries: 2,
        minDelay: 1000,
        factor: 2,
      };

      const promises = [
        pRetry(() => this.account.initialize(), retryOptions),
        pRetry(() => this.spaces.initialize(), retryOptions),
        pRetry(() => this.spaceAccounts.initialize(), retryOptions),
        pRetry(() => this.platformInfo.initialize(), retryOptions),
      ];

      /**
       * If it fails even after retries, we throw.
       */
      await Promise.all(promises);
      await this.platformInfo.assignIntentOnboardingIfEligible();
      this._initializeUserForMetricsAndLogs();

      /**
       * Trigger any side-effects related to initializing the UI client.
       * (Wiring up user info to our error-monitor, etc.)
       */
      await initializeAccountObservability({ store: this });
      await localDb.initializeUserDb();
      await this.initializeMemDB();
      void this.localBlobStore.init();
      await this.spaceAccountFeatureFlags.initialize();
      this.floatingAgent.initialize();
      await this.checkAndSetDefaultLogoutState();
      await this.initializeTanstackDatabase();

      // Load getting started progress
      await this.gettingStarted.load();
      await this.memAgent.claimIMessageLeadConnectionFromUrl();

      await this.calendarOnboarding.initialize();
      await this.onboarding.initialize();

      this.addPageLifecycleReactions();

      runInAction(() => {
        this.accountLoadingState = asyncResultModule.setReady(true);
      });
    } catch (unknownErr) {
      const err = unknownErr as BaseError;

      logger.error({
        message: "[initialize] AppStore failed to initialize.",
        info: { err: objectModule.safeErrorAsJson(err) },
      });

      runInAction(() => {
        this.accountLoadingState = asyncResultModule.setError(err);
      });

      throw err;
    }
  }

  _initializeUserForMetricsAndLogs() {
    const { myPersonalSpaceAccountId } = this.spaceAccounts;
    const isMemmer = isMemAccount(this.account.myAccount);

    const {
      profileEmailAddress: email,
      displayName: name,
      profilePhotoUrl,
      createdAt,
    } = this.account.myAccount;

    /**
     * We fire both of these off asynchronously.
     */
    if (myPersonalSpaceAccountId) {
      void electronModule.setClientMetadata({
        context: {
          space_account_id: myPersonalSpaceAccountId,
        },
      });

      void (async () => {
        const segmentIdentityEstablished = await identifyUserInMetrics({
          spaceAccountId: myPersonalSpaceAccountId,
          userProperties: {
            email,
            name,
            profilePhotoUrl,
            createdAt,
          },
          config: {
            isMemmer,
          },
        });
        if (!segmentIdentityEstablished) {
          return;
        }

        trackMobileWebLoginCompleted({
          accountEmail: email,
          spaceAccountId: myPersonalSpaceAccountId,
        });
        trackAccountSignupCompletedIfRecent({
          accountCreatedAt: createdAt,
          accountEmail: email,
          spaceAccountId: myPersonalSpaceAccountId,
        });
      })();
    }
  }

  async initializeMemDB() {
    logger.info({
      message: "[SYNC][MemDB] Initializing MemDB",
    });
    // Note: we don't have to open db connection here, it will be open on first request
    this._memDb = new MemDB(this.spaceAccounts.myPersonalSpaceAccountId);

    await this._memDb.persist();

    this.addDbListeners();
  }

  async checkAndSetDefaultLogoutState() {
    // LogoutState.LoggingOut means we interrupted a logout previously.
    // In that case, the safest bet is force user to login even if they still have auth token
    // Thats because we cannot guarantee how much data has been deleted
    const currentLogoutState = await this.memDb.settings.getLogoutState();
    logger.info({
      message: "[SYNC] Checking and setting default logout state",
      info: { currentLogoutState: currentLogoutState || "undefined" },
    });
    if (currentLogoutState !== LogoutState.LoggingOut) {
      await this.memDb.settings.setLogoutState(LogoutState.Idle);
    }
  }

  /**
   * Represents when the store is ready for use by child components.
   */
  get readyState(): AsyncResult<boolean> {
    const isError =
      this.accountLoadingState.error ||
      this.mountedState.error ||
      this.spaceAccountFeatureFlags.readyState.error;
    const isReady =
      this.accountLoadingState.data &&
      this.mountedState.data &&
      this.spaceAccountFeatureFlags.readyState.data;

    return {
      called: true,
      loading: !isReady,
      error: isError,
      data: isReady,
    } as AsyncResult<boolean>;
  }

  useInitializeAppStoreEffects = () => {
    this.queriesCache.useCacheCancellation();
    this.routing.useSyncRoutingParams();
    this.navigation.useSyncNavigation();
    this.settingsModal.useSyncSettingsSearchParam();
    this.agentOverlay.useSyncAgentSearchParam();
    this.memAgentCallout.useInitializeAppStoreEffects();
    this.featureGates.useInitializeAppStoreEffects();
    this.audio.recording.useInitializeAudioRecordingEffects();
    this.calendar.useInitializeCalendarEffects();

    useEffectOnMount(() => {
      const asyncLogic = async () => {
        try {
          await this.sync.initialize();
          await this.audio.initialize();

          /**
           * We set this in a useEffect because it should happen
           * -after- the initial render, so the `useEffect` hooks
           * from above have a chance to run first.
           */
          runInAction(() => {
            this.mountedState = asyncResultModule.setReady(true);
          });
          logger.info({ message: "[AppStore] Ready" });
        } catch (error) {
          const safeError = objectModule.safeErrorAsJson(error as Error);

          logger.error({
            message: "[AppStore] Failed to initialize",
            info: { error: safeError },
          });

          const err = new BaseError({
            message:
              "[AppStore.useInitializeAppStoreEffects] Failed to initialize.",
            info: {
              error: safeError,
            },
          });

          runInAction(() => {
            this.mountedState = asyncResultModule.setError(err);
          });
        }
      };

      asyncLogic();

      /**
       * We kick off any initializers on-mount.
       */
      return () => {
        if (import.meta.hot) {
          return;
        }
        /**
         * We only trigger the `unmount` side-effect if we're not hot-reloading.
         * During hot-reload, we can just keep the state as-is.
         */
        this.debug.resetState();
        this.account.resetState();
        this.notes.resetState();
        this.audio.resetState();
        this.calendar.resetState();
        this.spaces.resetState();
        this.spaceAccounts.resetState();
      };
    });
  };

  public forceHardReload = async () => {
    windowModule.forceHardReload();
  };

  public forceUpgradeClient = async () => {
    if (!clientEnvModule.isDesktop()) {
      setTimeout(() => {
        this.forceHardReload();
      }, 300);
      return;
    }

    const restartBlockers: DesktopUpdateRestartBlocker[] = [];

    if (this.audio.hasActiveRecording || this.audio.recording.isInitializing) {
      restartBlockers.push("recording");
    }

    await electronModule.updateNow({ restartBlockers, waitForUpdate: true });
  };

  public resetStorageAndReload = async ({
    preserveOutboundProcessingQueue = false,
    preserveLocalAudioRecoveryState = false,
  }: ResetStorageAndReloadOptions = {}) => {
    if (preserveLocalAudioRecoveryState) {
      await this.captureClientResetAudioRecoveryMarker();
    } else {
      await this.globalCache.del(CacheKey.ClientResetAudioRecovery);
    }

    await localDb.queue.clear();
    await localDb.syncUpdates.clear();
    await localDb.sync.clear();

    await this.memDb.syncUpdates.clear();
    if (!preserveOutboundProcessingQueue) {
      await this.memDb.queue.processing.clear();
    }
    // Only `processing` is safe and useful to preserve for CLIENT_RESET_REQUIRED.
    // `pending` has already been accepted by the backend, while
    // `optimisticUpdates` is derived UI state based on the old local cache. Both
    // should be rebuilt from the fresh bootstrap/sync stream after reload.
    await this.memDb.queue.pending.clear();
    await this.memDb.queue.optimisticUpdates.clear();
    await this.memDb.settings.clearNoteContentPreloadingState();
    await this.memDb.settings.clearNoteContentDocumentLastHydrateCursor();
    await this.memDb.settings.clearLastSyncId();
    await this.memDb.settings.clearFeatureFlags();

    if (!preserveLocalAudioRecoveryState) {
      await this.dexie.repositories.localAudioRecordingBlobs.clearAll();
      await this.dexie.repositories.localAudioRecordingTranscriptTurns.clearAll();
      await this.dexie.repositories.webAudioArtifacts.clearAll();
    }

    if (preserveOutboundProcessingQueue) {
      await this.localBlobStore.clearUploaded();
    } else {
      await this.localBlobStore.clearAll();
    }

    /**
     * Rotate the client + client-group identity.
     *
     * A reset is triggered when the server determines this client group is
     * out-of-date and can never sync again (e.g. CLIENT_RESET_REQUIRED from a
     * schema-migration version boundary). The server expects re-bootstrap to
     * produce a NEW client group id; if we keep the same one, the next sync hits
     * the same stale `SyncClientGroup` row and resets again, looping forever.
     */
    await clientEnvModule.clearClientId();
    await clientEnvModule.clearClientGroupId();

    setTimeout(() => {
      windowModule.forceReload();
    }, 300);
  };

  /**
   * Records enough local evidence to reconnect audio rows after client reset.
   *
   * CLIENT_RESET_REQUIRED intentionally rotates the client group ID below. The
   * audio boot reconciler uses `associated_client_id` to decide which local
   * recordings belong to this device, so preserving artifact tables alone would
   * leave pre-reset recordings invisible. This marker is intentionally narrow:
   * only rows that were local to the outgoing client group, referenced by local
   * payload tables, or referenced by unsent audio operations can be recovered.
   */
  private async captureClientResetAudioRecoveryMarker(): Promise<void> {
    const { clientGroupId: previousClientGroupId } =
      await clientEnvModule.clientGroupId();
    const existingMarker = await this.globalCache.get(
      CacheKey.ClientResetAudioRecovery,
    );
    const recoveryEvidence = await this.collectClientResetAudioRecoveryEvidence(
      {
        previousClientGroupId,
      },
    );
    const audioRecordingIds = new Set([
      ...(existingMarker?.audioRecordingIds ?? []),
      ...recoveryEvidence.audioRecordingIds,
    ]);
    const previousClientGroupIds = new Set(
      existingMarker?.previousClientGroupIds ?? [],
    );

    if (recoveryEvidence.hasOutgoingClientGroupRows) {
      previousClientGroupIds.add(previousClientGroupId);
    }

    if (audioRecordingIds.size === 0 || previousClientGroupIds.size === 0) {
      await this.globalCache.del(CacheKey.ClientResetAudioRecovery);
      return;
    }

    await this.globalCache.set(CacheKey.ClientResetAudioRecovery, {
      previousClientGroupIds: [...previousClientGroupIds].sort(),
      resetAt: new Date().toISOString(),
      audioRecordingIds: [...audioRecordingIds].sort(),
    });
  }

  private async collectClientResetAudioRecoveryEvidence({
    previousClientGroupId,
  }: {
    previousClientGroupId: Uuid;
  }): Promise<{
    audioRecordingIds: Uuid[];
    hasOutgoingClientGroupRows: boolean;
  }> {
    const [
      localAudioRows,
      blobRows,
      transcriptRows,
      webArtifactRows,
      processingOperations,
    ] = await Promise.all([
      this.audioRecordings.localTable
        .where("associated_client_id")
        .equals(previousClientGroupId)
        .filter((row) => this.isClientResetAudioRecoveryRow(row))
        .toArray(),
      this.dexie.repositories.localAudioRecordingBlobs.table.toArray(),
      this.dexie.repositories.localAudioRecordingTranscriptTurns.table.toArray(),
      this.memDb.localWebAudioArtifacts.toArray(),
      this.memDb.queue.processing.toArray(),
    ]);

    const ids = new Set<Uuid>();
    const hasOutgoingClientGroupRows = localAudioRows.length > 0;

    for (const row of localAudioRows) {
      ids.add(row.model_id as Uuid);
    }

    for (const row of blobRows) {
      ids.add(row.associatedAudioRecordingId);
    }

    for (const row of transcriptRows) {
      ids.add(row.associatedAudioRecordingId);
    }

    for (const row of webArtifactRows) {
      ids.add(row.key);
    }

    for (const operation of processingOperations) {
      if (
        !operation.modelId ||
        !this.isAudioRecordingOperationKind(operation.operationKind)
      ) {
        continue;
      }

      ids.add(operation.modelId as Uuid);
    }

    return {
      audioRecordingIds: [...ids].sort(),
      hasOutgoingClientGroupRows,
    };
  }

  private isClientResetAudioRecoveryRow(
    row: IndexedAudioRecordingSyncUpdateValue,
  ): boolean {
    if (row.trashed_at !== null) {
      return false;
    }

    const modelData = row.model_data;
    const hasTerminalUploadStatus =
      modelData.upload_status === AudioRecordingUploadStatus.COMPLETED ||
      modelData.upload_status === AudioRecordingUploadStatus.ERROR ||
      modelData.upload_status === AudioRecordingUploadStatus.SKIPPED;

    return (
      modelData.recording_status !== AudioRecordingRecordingStatus.COMPLETED ||
      !hasTerminalUploadStatus ||
      modelData.processing_status !== AudioRecordingProcessingStatus.COMPLETED
    );
  }

  private isAudioRecordingOperationKind(operationKind: string): boolean {
    return operationKind.endsWith("_AUDIO_RECORDING");
  }

  public resetAndReinitializeSync = async () => {
    this.stopSync();
    this.resetSync();
    await this.sync.initialize();
    await this.initializeTanstackDatabase();
  };

  stopSync = () => {
    this.db.stopSync().catch(() => {});

    for (const value of Object.values(this)) {
      if (value instanceof AppSubStore) {
        value.stopSync();
      }
    }
  };

  resetSync = () => {
    for (const value of Object.values(this)) {
      if (value instanceof AppSubStore) {
        value.resetSync();
      }
    }
  };

  initializeTanstackDatabase = async () => {
    try {
      const spaceAccountId = this.spaceAccounts.myPersonalSpaceAccountId;

      logger.info({
        message: "[initializeTanstackDatabase] Initializing database",
        info: { spaceAccountId },
      });

      await this.db.init({ spaceAccountId });
    } catch (unknownErr) {
      const err = unknownErr as BaseError;

      logger.error({
        message: "[initializeTanstackDatabase] Failed to initialize database",
        info: { err: objectModule.safeErrorAsJson(err) },
      });

      throw err;
    }
  };

  addDbListeners() {
    this.memDb.on("versionchange", () => {
      logger.info({
        message: "[MemDB] version changed",
      });

      // TODO: show modal dialog with "version changed, please refresh" message
      // const isDesktop = clientEnvModule.isDesktop();
    });
  }

  // When tab freezes we want to close db connection and allow other tabs (if any) to continue operating
  // If we don't, the app can get into the state it's no longer able to converse with indexedDb
  // Based on this recommendation: https://developer.chrome.com/docs/web-platform/page-lifecycle-api

  // When tab is hidden, we want to pause the Q
  // When tab exits hidden state, we want to resume the Q (assuming tab is not frozen)
  private addPageLifecycleReactions = () => {
    reaction(
      () => this.tabLifecycleManager.state,
      (tabState, previousTabState) => {
        if (tabState === TabLifecycleState.Frozen) {
          return this.onFreeze();
        }

        if (previousTabState === TabLifecycleState.Frozen) {
          return this.onUnfreeze();
        }

        if (tabState === TabLifecycleState.Hidden) {
          return this.onHide();
        }

        if (previousTabState === TabLifecycleState.Hidden) {
          return this.onShow();
        }
      },
    );
  };

  // when tab is hidden we want to pause the queue
  private onHide = () => {
    this.tabDetector.hasOtherOpenTabs().then((hasOtherTabs) => {
      if (!hasOtherTabs) {
        logger.debug({
          message: "[MemDB][Sync] No other tabs detected, skip pausing",
          info: {
            tabState: this.tabLifecycleManager.state,
            queueState: this.sync.actionQueue.processingState,
            tabId: this.tabDetector.tabId,
          },
        });

        return;
      }

      if (this.tabLifecycleManager.state !== TabLifecycleState.Hidden) {
        return;
      }

      logger.info({
        message: "[MemDB][Sync] Pausing queue",
        info: {
          tabState: this.tabLifecycleManager.state,
          queueState: this.sync.actionQueue.processingState,
          tabId: this.tabDetector.tabId,
        },
      });

      this.sync.actionQueue.pause();
    });
  };

  // when tab is shown we want to resume the queue
  private onShow = () => {
    void this.resumeQueueAfterShow();
  };

  private resumeQueueAfterShow = async () => {
    if (!(await this.ensureMemDbOpen())) {
      return;
    }

    if (!this.canResumeQueueInCurrentLifecycleState()) {
      return;
    }

    logger.info({
      message: "[MemDB][Sync] Resuming queue",
      info: {
        tabState: this.tabLifecycleManager.state,
        queueState: this.sync.actionQueue.processingState,
        tabId: this.tabDetector.tabId,
      },
    });

    this.sync.actionQueue.resume();
  };

  // when tab freezes we want to pause the queue and close the db connection
  private onFreeze = () => {
    logger.info({
      message: "[MemDB][Sync] Freezing app",
      info: {
        tabState: this.tabLifecycleManager.state,
        queueState: this.sync.actionQueue.processingState,
        tabId: this.tabDetector.tabId,
      },
    });

    this.sync.actionQueue.pause();
    this.memDb.close();
  };

  // when tab unfreeze we want to reopen db and potentially resume the queue
  private onUnfreeze = () => {
    logger.info({
      message: "[MemDB][Sync] Unfreezing app",
      info: {
        tabState: this.tabLifecycleManager.state,
        queueState: this.sync.actionQueue.processingState,
        tabId: this.tabDetector.tabId,
      },
    });

    void this.resumeQueueAfterUnfreeze();
  };

  private resumeQueueAfterUnfreeze = async () => {
    if (!(await this.ensureMemDbOpen())) {
      return;
    }

    try {
      await this.resumeAndPrefillQueueIfTabNotHidden();
    } catch (error) {
      this.pauseQueueAfterForegroundRecoveryFailure(
        "[MemDB][Sync] Failed to refresh queue during foreground recovery",
        error,
      );
    }
  };

  // if we are not hidden, we want to resume the queue and prefill it with latest from disk
  private resumeAndPrefillQueueIfTabNotHidden = async () => {
    // grace period
    await sleep(2000);

    if (!this.canResumeQueueInCurrentLifecycleState()) {
      logger.info({
        message:
          "[MemDB][Sync] App is not ready, skipping resuming and prefilling queue",
        info: {
          dbIsOpen: this.memDb.isOpen(),
          tabState: this.tabLifecycleManager.state,
          queueState: this.sync.actionQueue.processingState,
          tabId: this.tabDetector.tabId,
        },
      });
      return;
    }

    logger.info({
      message:
        "[MemDB][Sync] Tab is not hidden, proceeding resume and prefill queue",
      info: {
        tabState: this.tabLifecycleManager.state,
        queueState: this.sync.actionQueue.processingState,
        tabId: this.tabDetector.tabId,
      },
    });

    await this.sync.actionQueue.updateFromLocal();

    if (!this.canResumeQueueInCurrentLifecycleState()) {
      return;
    }

    this.sync.actionQueue.resume();
  };

  private canResumeQueueInCurrentLifecycleState = () => {
    return this.memDb.isOpen() && this.isForegroundLifecycleState();
  };

  private isForegroundLifecycleState = () => {
    const tabState = this.tabLifecycleManager.state;

    return (
      tabState === TabLifecycleState.Active ||
      tabState === TabLifecycleState.Passive
    );
  };

  private ensureMemDbOpen = async (): Promise<boolean> => {
    if (this.memDbOpenPromise) {
      return this.memDbOpenPromise;
    }

    if (this.memDb.isOpen()) {
      return true;
    }

    this.sync.actionQueue.pause();
    this.memDbOpenPromise = pRetry(
      async () => {
        if (!this.isForegroundLifecycleState() || this.memDb.isOpen()) {
          return;
        }

        await this.memDb.open();
      },
      {
        retries: 2,
        minTimeout: 10,
        maxTimeout: 100,
        factor: 10,
        shouldRetry: () => this.isForegroundLifecycleState(),
      },
    )
      .then(() => {
        if (!this.isForegroundLifecycleState()) {
          this.memDb.close();
          return false;
        }

        return this.memDb.isOpen();
      })
      .catch((error) => {
        if (this.isForegroundLifecycleState()) {
          this.pauseQueueAfterForegroundRecoveryFailure(
            "[MemDB][Sync] Failed to open MemDB during foreground recovery",
            error,
          );
        }

        return false;
      })
      .finally(() => {
        this.memDbOpenPromise = null;
      });

    return this.memDbOpenPromise;
  };

  private pauseQueueAfterForegroundRecoveryFailure = (
    message: string,
    error: unknown,
  ) => {
    this.sync.actionQueue.pause();
    logger.error({
      message,
      info: {
        error: objectModule.safeErrorAsJson(error as Error),
        tabState: this.tabLifecycleManager.state,
        queueState: this.sync.actionQueue.processingState,
        tabId: this.tabDetector.tabId,
      },
    });
  };
}
